import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import BasicInfoStep from './steps/BasicInfoStep';
import CreateGDAPStep from './steps/CreateGDAPStep';
import CustomerApprovalStep from './steps/CustomerApprovalStep';
import FinalizeSetupStep from './steps/FinalizeSetupStep';
import AppConsentStep from './steps/AppConsentStep';
import CompleteStep from './steps/CompleteStep';
import { getClient } from '../../services/api';

export interface OnboardingState {
  // Step 1: Basic Info
  companyName: string;
  primaryContact: string;
  email: string;
  isExistingClient: boolean;  // true if upgrading GDAP for existing client
  existingClientId: string | null;  // Pax8 client ID if existing
  existingTenantId: string | null;  // MS tenant ID if known
  existingTenantDomain: string | null;  // MS tenant domain if known

  // Step 2: GDAP Creation
  relationshipId: string | null;
  approvalUrl: string | null;
  gdapStatus: string;

  // Step 3: Customer Approval
  customerTenantId: string | null;
  customerDisplayName: string | null;

  // Step 4: AdminAgents
  adminAgentsAssigned: boolean;

  // Step 5: App Consent
  appConsentGranted: boolean;
  exchangeRoleAssigned: boolean;

  // Client record
  clientId: string | null;
}

const STEPS = [
  { id: 1, name: 'Basic Info', description: 'Company details' },
  { id: 2, name: 'Create GDAP', description: 'Request admin access' },
  { id: 3, name: 'Approval', description: 'Customer approves' },
  { id: 4, name: 'Finalize', description: 'Assign security group' },
  { id: 5, name: 'Consent', description: 'Grant app access' },
  { id: 6, name: 'Complete', description: 'Setup finished' },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [initialized, setInitialized] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<OnboardingState>({
    companyName: '',
    primaryContact: '',
    email: '',
    isExistingClient: false,
    existingClientId: null,
    existingTenantId: null,
    existingTenantDomain: null,
    relationshipId: null,
    approvalUrl: null,
    gdapStatus: '',
    customerTenantId: null,
    customerDisplayName: null,
    adminAgentsAssigned: false,
    appConsentGranted: false,
    exchangeRoleAssigned: false,
    clientId: null,
  });

  // Auto-populate from URL params when coming from Client Detail page
  useEffect(() => {
    if (initialized) return;
    const clientId = searchParams.get('clientId');
    const clientName = searchParams.get('clientName');
    const tenantId = searchParams.get('tenantId');
    const tenantDomain = searchParams.get('tenantDomain');

    if (clientId && clientName) {
      // Coming from a client page — fetch full client data and skip to step 2
      (async () => {
        try {
          const client = await getClient(clientId);
          const contact = client.contacts?.[0];
          setState(prev => ({
            ...prev,
            companyName: client.companyName || clientName,
            primaryContact: contact?.name || '',
            email: contact?.email || '',
            isExistingClient: true,
            existingClientId: clientId,
            existingTenantId: client.tenantId || tenantId || null,
            existingTenantDomain: client.tenantDomain || tenantDomain || null,
            clientId: clientId,
          }));
          setCurrentStep(2); // Skip Basic Info — we already know the client
        } catch {
          // If fetch fails, still pre-populate what we have from URL params
          setState(prev => ({
            ...prev,
            companyName: clientName,
            isExistingClient: true,
            existingClientId: clientId,
            existingTenantId: tenantId || null,
            existingTenantDomain: tenantDomain || null,
            clientId: clientId,
          }));
          setCurrentStep(2);
        }
        setInitialized(true);
      })();
    } else {
      setInitialized(true);
    }
  }, [searchParams, initialized]);

  const updateState = useCallback((updates: Partial<OnboardingState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const nextStep = useCallback(() => {
    setError(null);
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
  }, []);

  const prevStep = useCallback(() => {
    setError(null);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  }, []);

  // If we came from a client page (URL has clientId), don't allow going back to step 1
  const cameFromClient = !!searchParams.get('clientId');
  const canGoBack = currentStep > (cameFromClient ? 2 : 1) && currentStep < 6;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoStep
            state={state}
            updateState={updateState}
            onNext={nextStep}
            setError={setError}
          />
        );
      case 2:
        return (
          <CreateGDAPStep
            state={state}
            updateState={updateState}
            onNext={nextStep}
            setError={setError}
          />
        );
      case 3:
        return (
          <CustomerApprovalStep
            state={state}
            updateState={updateState}
            onNext={nextStep}
            setError={setError}
          />
        );
      case 4:
        return (
          <FinalizeSetupStep
            state={state}
            updateState={updateState}
            onNext={nextStep}
            setError={setError}
          />
        );
      case 5:
        return (
          <AppConsentStep
            state={state}
            updateState={updateState}
            onNext={nextStep}
            setError={setError}
          />
        );
      case 6:
        return <CompleteStep state={state} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(cameFromClient ? `/clients/${searchParams.get('clientId')}` : '/clients')}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-foreground">
                  Client Onboarding
                </h1>
                <p className="text-sm text-muted-foreground">
                  {cameFromClient ? `Set up GDAP for ${state.companyName || 'client'}` : 'Add a new client with GDAP access'}
                </p>
              </div>
            </div>
            {state.companyName && (
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">
                  {state.companyName}
                </p>
                {state.customerTenantId && (
                  <p className="text-xs text-muted-foreground">
                    Tenant: {state.customerTenantId.substring(0, 8)}...
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <nav aria-label="Progress">
            <ol className="flex items-center">
              {STEPS.map((step, index) => (
                <li
                  key={step.id}
                  className={`${index !== STEPS.length - 1 ? 'flex-1' : ''} relative`}
                >
                  <div className="flex items-center">
                    <div
                      className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                        currentStep > step.id
                          ? 'bg-blue-600'
                          : currentStep === step.id
                          ? 'border-2 border-blue-600 bg-card'
                          : 'border-2 border-border bg-card'
                      }`}
                    >
                      {currentStep > step.id ? (
                        <CheckCircle className="h-5 w-5 text-white" />
                      ) : (
                        <span
                          className={`text-sm font-medium ${
                            currentStep === step.id
                              ? 'text-blue-600'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {step.id}
                        </span>
                      )}
                    </div>
                    {index !== STEPS.length - 1 && (
                      <div
                        className={`ml-4 flex-1 h-0.5 ${
                          currentStep > step.id
                            ? 'bg-blue-600'
                            : 'bg-border'
                        }`}
                      />
                    )}
                  </div>
                  <div className="mt-2 hidden sm:block">
                    <p
                      className={`text-xs font-medium ${
                        currentStep >= step.id
                          ? 'text-blue-600'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {step.name}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                  Error
                </h3>
                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="card p-6">
          {!initialized ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-muted-foreground">Loading client data...</span>
            </div>
          ) : (
            renderStepContent()
          )}
        </div>

        {/* Back Button (outside of card) */}
        {canGoBack && (
          <div className="mt-4">
            <button
              onClick={prevStep}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              &larr; Back to previous step
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
